<template>
  <div>
    <div class="wrapper">
      <breadcrumb :crumbs="[$t('menu.bargains'), $t('text.list')]">
        <el-button
          v-if="Permit.canActions('rentManage')"
          type="success"
          size="mini"
          @click="
            Permit.isFreeEdition()
              ? checkTenantValid('rentsNew')
              : create('rentsNew')
          "
          >+ {{ $t("text.create") }}{{ $t("itemTypes.rent") }}</el-button
        >
        <el-button
          v-if="Permit.canActions('rentManage')"
          type="primary"
          size="mini"
          @click="
            Permit.isFreeEdition()
              ? checkTenantValid('ordersNew')
              : create('ordersNew')
          "
          >+ {{ $t("text.create") }}{{ $t("itemTypes.order") }}</el-button
        >
        <el-button
          v-if="Permit.canActions('rentManage')"
          type="warning"
          size="mini"
          @click="
            Permit.isFreeEdition()
              ? checkTenantValid('cleansNew')
              : create('cleansNew')
          "
          >+ {{ $t("text.create") }}{{ $t("itemTypes.clean") }}</el-button
        >

        <el-button
          v-if="Permit.canActions('rentManage')"
          type="default"
          size="mini"
          @click="$router.push('/normal/rents/import')"
          >{{ $t("action.import") }}</el-button
        >
      </breadcrumb>
    </div>
    <narrow
      :narrows="narrows"
      :init-filter="initFilter"
      @narrowSelect="narrowSelect"
    />
    <div class="search with-narrows">
      <el-form
        :inline="true"
        @submit.native.prevent="search"
        :model="searchForm"
        size="small"
      >
        <el-form-item>
          <el-input
            size="small"
            v-model.trim="searchForm.keyword"
            :placeholder="$t('placeholder.serialNoCustomer')"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            size="small"
            v-model.trim="searchForm.productNo"
            :placeholder="$t('product.productNo')"
          />
        </el-form-item>
        <el-form-item>
          <el-select
            size="small"
            v-model="searchForm.shopId"
            :placeholder="$t('menu.shops')"
            filterable
            clearable
          >
            <el-option
              v-for="shop in shops"
              :key="shop.id"
              :label="shop.name"
              :value="shop.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <date-range
            size="small"
            v-model="searchForm"
            type="datetime"
            field="createAt"
            :start-placeholder="$t('rules.startAt', [$t('text.createAt')])"
            :end-placeholder="$t('rules.endAt', [$t('text.createAt')])"
          />
        </el-form-item>
        <el-form-item>
          <date-range
            size="small"
            v-model="searchForm"
            type="datetime"
            field="rentAt"
            :start-placeholder="$t('rules.startAt', [$t('rent.rentAt')])"
            :end-placeholder="$t('rules.endAt', [$t('rent.rentAt')])"
          />
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-search"
            native-type="submit"
          />
        </el-form-item>
      </el-form>
    </div>

    <div class="wrapper table-area">
      <sticky
        :sticky-top="62"
        :viewport="1024"
        :is-rem="false"
        style="min-height: 52px"
      >
        <vxe-toolbar ref="recordsToolbar" custom zoom class="table-toolbar">
          <template v-slot:buttons>
            <el-dropdown class="mgl10" @command="download">
              <el-button type="default" size="mini">
                {{ $t("action.download") }}
                <i class="el-icon-caret-bottom el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="checked">{{
                  $t("action.downLoadChecked")
                }}</el-dropdown-item>
                <el-dropdown-item command="search">{{
                  $t("action.downLoadSearch")
                }}</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <span class="pager-total">
              {{ $t("rules.pagerTotal", [pager.total]) }}
            </span>
          </template>
        </vxe-toolbar>
      </sticky>
      <vxe-table
        :data="records"
        @checkbox-change="selectRecord"
        @checkbox-all="selectAllRecords"
        :checkbox-config="{ reserve: true }"
        @sort-change="sortRecords"
        :sort-config="{ remote: true }"
        :custom-config="{ storage: true, checkMethod: checkColumnMethod }"
        :span-method="mergeMethod"
        :loading="loading"
        id="bargainsTable"
        row-id="id"
        ref="recordsTable"
        class="recordsTable"
        :row-class-name="calculateBgColor"
        size="mini"
        resizable
        auto-resize
        highlight-hover-row
        :scroll-y="{ enabled: false }"
      >
        <vxe-table-column
          field="checkbox"
          type="checkbox"
          width="35"
        ></vxe-table-column>
        <vxe-table-column field="type" :title="$t('text.type')" width="94">
          <template v-slot="{ row }">
            <span v-if="!row.isMerge">
              <div :class="'tag-type tag-type-' + row.type">
                {{ $t("itemTypes." + row.type) }}
              </div>
            </span>
            <div v-else>
              <span v-if="row.type == 'rent'">
                <span v-if="row.hurry">{{ $t("order.hurrySimple") }}</span>
                <span class="a mgr5" @click="showRent(row)">
                  {{ row.serialNo }}
                </span>
                <span v-if="row.packageId != null" class="mgr5 strong">
                  {{ row.packageName }}
                </span>
                <span>
                  <span class="font10 mgr5">
                    {{ $t("text.total") }}:
                    {{ row.amount | currency }}
                  </span>
                  <span class="font10 mgr5">
                    {{ $t("payment.receive") }}:
                    {{ row.payAmount | currency }}
                  </span>
                  <template class="mgr5">
                    <span v-if="row.diffAmount > 0" class="red font10">
                      {{ $t("payment.differ") }}:
                      {{ row.diffAmount | currency }}
                    </span>
                    <span v-if="row.diffAmount == 0" class="green font10">
                      {{ $t("payment.settled") }}
                    </span>
                    <span v-if="row.diffAmount < 0" class="orange font10">
                      {{ $t("payment.leave") }}:{{ -row.diffAmount | currency }}
                    </span>
                  </template>

                  <span class="font10 mgr5 orange" v-if="row.deposit != null">
                    {{ $t("rent.deposit") }}:
                    {{ row.depositAmount | currency }}
                    <span v-if="row.depositReturnAmount != null" class="green">
                      <!-- {{ $t("action.returnDeposit") }}:{{
                        row.depositReturnAmount | currency
                      }} -->
                      押金已退
                    </span>
                  </span>
                </span>
                <span class="float-right font10">
                  {{ $t("menu.customers") }}：{{ row.customerName }}
                  <span v-if="row.customerWeddingDay != null">
                    {{ $t("customer.weddingDay") }}：{{
                      row.customerWeddingDay
                    }}
                  </span>
                  {{ $t("text.owner") }}:{{ row.ownerName }}
                  {{ row.shopName }}
                </span>
              </span>
              <span v-if="row.type == 'clean'">
                <span class="a mgr5" @click="showRent(row)">
                  {{ row.serialNo }}
                </span>
                <span class="font10 mgr5" v-if="row.serviceName != null">
                  {{ $t("rent.serviceName") }}:{{ row.serviceName }}
                </span>
                <span class="font10 mgr5" v-if="row.serviceAmount != null">
                  {{ $t("rent.serviceAmount") }}:{{
                    row.serviceAmount | currency
                  }}
                </span>

                <span class="float-right font10">
                  {{ $t("text.owner") }}:{{ row.ownerName }} {{ row.shopName }}
                </span>
              </span>
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="images"
          :title="$t('product.images')"
          width="80"
        >
          <template v-slot="{ row }">
            <thumbnail :images="row.images" />
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="serialNo"
          :title="$t('rentItem.serialNo')"
          min-width="100"
        >
          <template v-slot="{ row }">
            <span class="a" @click="showItem(row)">
              {{ row.serialNo }}
            </span>
            <div>
              <state-label
                :state="row.workflowState"
                v-if="['rent', 'clean', 'service'].includes(row.type)"
              >
                {{ $t("rentStates." + row.workflowState) }}
              </state-label>
              <state-label :state="row.workflowState" v-else>
                {{ $t("orderStates." + row.workflowState) }}
              </state-label>
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="productNo"
          :title="$t('product.productNo')"
          min-width="100"
        >
          <template v-slot="{ row }">
            {{ row.productNo }}
            <div class="font10">
              {{ row.categoryName }}<span v-if="row.brand !== null">/</span
              >{{ row.brand }}
            </div>
            <div class="font10">{{ row.color }} {{ row.size }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="productPrice"
          :title="$t('product.salesPrice')"
          min-width="70"
        >
          <template v-slot="{ row }">
            <span
              v-if="
                row.type != 'clean' && row._rent && row._rent.packageId == null
              "
            >
              {{ row.productPrice | currency }}
            </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="price"
          :title="$t('text.price2')"
          min-width="70"
        >
          <template v-slot="{ row }">
            <span
              v-if="
                row.type != 'clean' && row._rent && row._rent.packageId == null
              "
            >
              {{ row.price | currency }}
            </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="quantity"
          :title="$t('text.quantity')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.quantity }}{{ row.unit || "" }}

            <template v-if="['custom', 'available'].includes(row.type)">
              <el-popover
                placement="right"
                width="460"
                trigger="click"
                @show="getItemReceives(row)"
              >
                <vxe-table :data="itemReceives" size="mini">
                  <vxe-table-column
                    field="quantity"
                    :title="$t('text.quantity')"
                    min-width="50"
                  />
                  <vxe-table-column
                    field="ownerName"
                    :title="$t('order.receiveName')"
                    min-width="70"
                  />
                  <vxe-table-column
                    field="createAt"
                    :title="$t('order.receiveAt')"
                    width="180"
                  />
                  <vxe-table-column
                    field="remark"
                    :title="$t('text.remark')"
                    min-width="80"
                  >
                    <template v-slot="{ row }">
                      <raw-html-limit-number
                        :content="row.remark"
                        :number="12"
                      />
                    </template>
                  </vxe-table-column>
                  <vxe-table-column
                    field="voucher"
                    :title="$t('payment.voucher')"
                    min-width="80"
                  >
                    <template v-slot="{ row }">
                      <thumbnail-text :srcs="row.voucher" />
                    </template>
                  </vxe-table-column>
                </vxe-table>
                <div
                  slot="reference"
                  class="popover-reference font10"
                  v-if="row.receiveQuantity != 0"
                >
                  {{ $t("action.receive") }}:{{ row.receiveQuantity
                  }}{{ row.unit || "" }}
                </div>
              </el-popover>
              <el-popover
                placement="right"
                width="460"
                trigger="click"
                @show="getItemQualities(row)"
              >
                <vxe-table :data="itemQualities" size="mini">
                  <vxe-table-column
                    field="quantity"
                    :title="$t('text.quantity')"
                    min-width="50"
                  />
                  <vxe-table-column
                    field="ownerName"
                    :title="$t('order.quantityName')"
                    min-width="70"
                  />
                  <vxe-table-column
                    field="createAt"
                    :title="$t('order.quantityAt')"
                    width="180"
                  />
                  <vxe-table-column
                    field="remark"
                    :title="$t('text.remark')"
                    min-width="80"
                  >
                    <template v-slot="{ row }">
                      <raw-html-limit-number
                        :content="row.remark"
                        :number="12"
                      />
                    </template>
                  </vxe-table-column>
                  <vxe-table-column
                    field="voucher"
                    :title="$t('payment.voucher')"
                    min-width="80"
                  >
                    <template v-slot="{ row }">
                      <thumbnail-text :srcs="row.voucher" />
                    </template>
                  </vxe-table-column>
                </vxe-table>
                <div
                  slot="reference"
                  class="popover-reference font10"
                  v-if="row.qualityQuantity != 0"
                >
                  {{ $t("action.quality") }}:{{ row.qualityQuantity
                  }}{{ row.unit || "" }}
                </div>
              </el-popover>
              <el-popover
                placement="right"
                width="460"
                trigger="click"
                @show="getItemReworks(row)"
              >
                <vxe-table :data="itemReworks" size="mini">
                  <vxe-table-column
                    field="quantity"
                    :title="$t('text.quantity')"
                    min-width="50"
                  />
                  <vxe-table-column
                    field="ownerName"
                    :title="$t('order.reworkName')"
                    min-width="70"
                  />
                  <vxe-table-column
                    field="createAt"
                    :title="$t('order.reworkAt')"
                    width="180"
                  />
                  <vxe-table-column
                    field="remark"
                    :title="$t('text.remark')"
                    min-width="80"
                  >
                    <template v-slot="{ row }">
                      <raw-html-limit-number
                        :content="row.remark"
                        :number="12"
                      />
                    </template>
                  </vxe-table-column>
                  <vxe-table-column
                    field="voucher"
                    :title="$t('payment.voucher')"
                    min-width="80"
                  >
                    <template v-slot="{ row }">
                      <thumbnail-text :srcs="row.voucher" />
                    </template>
                  </vxe-table-column>
                </vxe-table>

                <div
                  slot="reference"
                  class="popover-reference font10"
                  v-if="row.reworkQuantity != 0"
                >
                  {{ $t("action.rework") }}:{{ row.reworkQuantity
                  }}{{ row.unit || "" }}
                </div>
              </el-popover>
            </template>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="endAt"
          :title="$t('rent.rentAtHandOverAt')"
          min-width="110"
        >
          <template v-slot="{ row }">
            <span v-if="['rent', 'clean', 'service'].includes(row.type)">
              <div class="font10">{{ row.startAt }}</div>
              <div class="font10">{{ row.endAt }}</div>
            </span>
            <span>{{ row.handOverAt }}</span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="outName"
          :title="$t('rentItem.outName')"
          min-width="96"
        >
          <template v-slot="{ row }">
            {{ row.outName }}
            <div class="font10">{{ row.outAt }}</div>
          </template>
        </vxe-table-column>

        <vxe-table-column
          field="finishName"
          :title="$t('rentItem.finishName')"
          min-width="96"
        >
          <template v-slot="{ row }">
            {{ row.finishName }}
            <div class="font10">{{ row.finishAt }}</div>
          </template>
        </vxe-table-column>

        <vxe-table-column
          field="createAt"
          :title="$t('text.createAt')"
          min-width="110"
        />
        <vxe-table-column
          field="remark"
          :title="$t('text.remark')"
          min-width="80"
        >
          <template v-slot="{ row }">
            <raw-html-limit-number
              :content="row.remark"
              :number="10"
              type="richText"
            />
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="action"
          :title="$t('text.action')"
          width="140"
          fixed="right"
        >
          <template v-slot="{ row, rowIndex }">
            <template v-if="row.isMerge">
              <el-dropdown>
                <a class="el-dropdown-link small-link">
                  {{ $t("action.print") }}
                  <i class="el-icon-arrow-down el-icon--right"></i>
                </a>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item>
                    <div
                      class="font10"
                      @click="printSlip(row, 'printRentSlipDiv')"
                    >
                      {{ $t("action.print") }}1
                    </div>
                  </el-dropdown-item>
                  <el-dropdown-item>
                    <div
                      class="font10"
                      @click="printSlip(row, 'printRentSlipDiv2')"
                    >
                      {{ $t("action.print") }}2(无图片)
                    </div>
                  </el-dropdown-item>
                  <el-dropdown-item>
                    <div
                      class="font10"
                      @click="printSlip(row, 'printRentSlipDiv3')"
                    >
                      {{ $t("action.print") }}3(无图片)
                    </div>
                  </el-dropdown-item>

                  <el-dropdown-item
                    v-for="(pt, index) of printTemplatesRent"
                    :key="index"
                    :divided="index == 0 ? true : false"
                  >
                    <div class="font10" @click="printSlip(row, pt)">
                      {{ pt.name }}
                    </div>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <el-dropdown class="mgl10">
                <a class="el-dropdown-link small-link">
                  {{ $t("text.more") }}
                  <i class="el-icon-arrow-down el-icon--right"></i>
                </a>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item
                    v-if="
                      canStockInAll(row) == true &&
                      Permit.canActions('rentStockIn')
                    "
                  >
                    <div class="font10" @click="inAll(row)">
                      {{ $t("action.stockIn2") }}
                    </div>
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="
                      row.type == 'clean' &&
                      canStockOutAllClean(row) == true &&
                      Permit.canActions('rentStockOut')
                    "
                  >
                    <!-- &&
                      row.diffAmount <= 0 -->
                    <div class="font10" @click="outAll(row)">
                      {{ $t("action.stockOut2") }}
                    </div>
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="
                      row.type == 'rent' &&
                      canStockOutAllRent(row) == true &&
                      Permit.canActions('rentStockOut')
                    "
                  >
                    <!-- &&
                      row.diffAmount <= 0 -->
                    <div class="font10" @click="deliveryAll(row)">
                      {{ $t("action.deliveryOut2") }}
                    </div>
                  </el-dropdown-item>
                  <template v-if="Permit.canActions('rentManage')">
                    <el-dropdown-item v-if="row.type == 'clean'">
                      <div
                        class="font10"
                        @click="
                          $router.push('/normal/cleans/new-item/' + row.id)
                        "
                      >
                        {{ $t("action.addClean") }}
                      </div>
                    </el-dropdown-item>
                    <template v-else>
                      <el-dropdown-item>
                        <div
                          class="font10"
                          @click="
                            $router.push('/normal/rents/new-item/' + row.id)
                          "
                        >
                          {{ $t("action.addRent") }}
                        </div>
                      </el-dropdown-item>
                      <el-dropdown-item>
                        <div
                          class="font10"
                          @click="
                            $router.push('/normal/services/new-item/' + row.id)
                          "
                        >
                          {{ $t("action.addService") }}
                        </div>
                      </el-dropdown-item>
                      <el-dropdown-item>
                        <div
                          class="font10"
                          @click="
                            $router.push('/normal/orders/new-item/' + row.id)
                          "
                        >
                          {{ $t("action.addOrder") }}
                        </div>
                      </el-dropdown-item>
                      <el-dropdown-item v-if="Permit.canActions('rentManage')">
                        <div class="font10" @click="addPayment(row, rowIndex)">
                          {{ $t("action.addReceiveAndPayment") }}
                        </div>
                      </el-dropdown-item>
                      <el-dropdown-item
                        v-if="
                          Permit.canActions('rentManage') && row.deposit == null
                        "
                      >
                        <div class="font10" @click="addDeposit(row, rowIndex)">
                          {{ $t("action.addDeposit") }}
                        </div>
                      </el-dropdown-item>
                    </template>
                    <el-dropdown-item v-if="canEditRent(row)">
                      <div class="font10" @click="editRent(row, rowIndex)">
                        {{ $t("text.edit") }}
                      </div>
                    </el-dropdown-item>
                  </template>
                  <el-dropdown-item
                    v-if="Permit.canActions('rentDistributiveOwner')"
                  >
                    <div
                      class="font10"
                      @click="distributiveOwner(row, rowIndex)"
                    >
                      {{ $t("action.distributiveOwner") }}
                    </div>
                  </el-dropdown-item>

                  <el-dropdown-item
                    v-if="
                      row.items &&
                      row.items.length == 0 &&
                      row.orders &&
                      row.orders.length == 0 &&
                      Permit.canActions('rentDelete')
                    "
                  >
                    <el-popconfirm
                      :title="$t('prompt.confirmDelete')"
                      @confirm="deleteRent(row)"
                    >
                      <div slot="reference" class="font10">
                        {{ $t("text.delete") }}
                      </div>
                    </el-popconfirm>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
            <template v-else>
              <span v-if="['rent', 'clean'].includes(row.type)">
                <el-button
                  v-if="
                    row.workflowState === 'newly' &&
                    Permit.canActions('rentManage')
                  "
                  type="text"
                  size="mini"
                  @click="editItem(row, rowIndex)"
                >
                  {{ $t("text.edit") }}
                </el-button>
                <el-popconfirm
                  v-if="
                    row.workflowState === 'newly' &&
                    Permit.canActions('rentDelete')
                  "
                  class="mgl10 mgr10"
                  :title="$t('prompt.confirmDelete')"
                  @confirm="deleteItem(row)"
                >
                  <el-button slot="reference" type="text" size="mini">{{
                    $t("text.delete")
                  }}</el-button>
                </el-popconfirm>
                <el-button
                  v-if="
                    row.workflowState === 'newly' &&
                    Permit.canActions('rentCancel')
                  "
                  type="text"
                  size="mini"
                  @click="cancelItem(row)"
                >
                  {{ $t("action.cancel") }}
                </el-button>
                <div>
                  <!--  && !(row._rent && row._rent.diffAmount < 0) -->
                  <el-button
                    v-if="
                      row.type == 'clean' &&
                      row.workflowState === 'newly' &&
                      Permit.canActions('rentStockOut')
                    "
                    type="text"
                    size="mini"
                    @click="outItem(row)"
                    >{{ $t("action.stockOut2") }}</el-button
                  >

                  <el-button
                    v-if="
                      row.type == 'rent' &&
                      row.workflowState === 'newly' &&
                      Permit.canActions('rentPrepare')
                    "
                    type="text"
                    size="mini"
                    @click="prepareItemRent(row)"
                    >{{ $t("action.prepareRent") }}</el-button
                  >
                  <el-button
                    v-if="
                      row.type == 'rent' &&
                      row.workflowState === 'preparing' &&
                      Permit.canActions('rentPrepared')
                    "
                    type="text"
                    size="mini"
                    @click="preparedItemRent(row)"
                    >{{ $t("action.prepared") }}</el-button
                  >
                  <!-- &&
                      !(row._rent && row._rent.diffAmount < 0) -->
                  <el-button
                    v-if="
                      row.type == 'rent' &&
                      row.workflowState === 'prepared' &&
                      Permit.canActions('rentStockOut')
                    "
                    type="text"
                    size="mini"
                    @click="deliveryItem(row)"
                    >{{ $t("action.deliveryOut2") }}</el-button
                  >

                  <el-button
                    v-if="
                      row.workflowState == 'outed' &&
                      Permit.canActions('rentStockIn')
                    "
                    type="text"
                    size="mini"
                    @click="inItem(row, 'item')"
                    >{{ $t("action.stockIn2") }}</el-button
                  >
                </div>
              </span>
              <span v-if="['service'].includes(row.type)">
                <template v-if="row.workflowState === 'newly'">
                  <el-button
                    v-if="Permit.canActions('rentManage')"
                    type="text"
                    size="mini"
                    @click="editItem(row, rowIndex)"
                  >
                    {{ $t("text.edit") }}
                  </el-button>
                  <el-popconfirm
                    v-if="Permit.canActions('rentDelete')"
                    class="mgl10 mgr10"
                    :title="$t('prompt.confirmDelete')"
                    @confirm="deleteItem(row)"
                  >
                    <el-button slot="reference" type="text" size="mini">{{
                      $t("text.delete")
                    }}</el-button>
                  </el-popconfirm>
                  <el-button
                    v-if="Permit.canActions('rentCancel')"
                    type="text"
                    size="mini"
                    @click="cancelItem(row)"
                  >
                    {{ $t("action.cancel") }}
                  </el-button>
                  <div>
                    <el-button
                      type="text"
                      size="mini"
                      @click="inItem(row)"
                      v-if="Permit.canActions('rentStockIn')"
                      >{{ $t("action.finish") }}</el-button
                    >
                  </div>
                </template>
              </span>
              <span v-if="['custom', 'available'].includes(row.type)">
                <template v-if="row.type == 'available'">
                  <el-button
                    v-if="
                      row.workflowState == 'newly' &&
                      Permit.canActions('rentPrepare')
                    "
                    type="text"
                    size="mini"
                    @click="prepareItem(row)"
                    >{{ $t("action.prepare") }}</el-button
                  >
                  <el-button
                    v-if="
                      row.workflowState == 'preparing' &&
                      Permit.canActions('rentPrepared')
                    "
                    type="text"
                    size="mini"
                    @click="preparedItem(row)"
                  >
                    {{ $t("orderStates.prepared") }}</el-button
                  >
                  <el-button
                    v-if="
                      row.workflowState == 'newly' &&
                      Permit.canActions('spotDelivery')
                    "
                    type="text"
                    size="mini"
                    @click="availableDeliveryItem(row)"
                    >{{ $t("action.availableDelivery") }}</el-button
                  >
                  <el-button
                    v-if="
                      row.workflowState == 'prepared' &&
                      Permit.canActions('rentStockOut') &&
                      !(row.rent && row._rent.diffAmount < 0)
                    "
                    type="text"
                    size="mini"
                    @click="deliveryItem(row)"
                    >{{ $t("action.delivery") }}</el-button
                  >
                </template>
                <template v-if="row.type == 'custom'">
                  <el-button
                    v-if="
                      row.workflowState == 'newly' &&
                      Permit.canActions('rentToProcessing')
                    "
                    type="text"
                    size="mini"
                    @click="processingItem(row)"
                    >{{ $t("action.processing") }}</el-button
                  >
                  <el-button
                    v-if="
                      ['processing', 'reworking'].includes(row.workflowState) &&
                      row.canReceiveQuantity != 0 &&
                      Permit.canActions('rentReceive')
                    "
                    type="text"
                    size="mini"
                    @click="receiveItem(row)"
                    >{{ $t("action.receive") }}</el-button
                  >
                  <el-button
                    v-if="
                      ['processing', 'received', 'reworking'].includes(
                        row.workflowState
                      ) &&
                      row.canQualityQuantity != 0 &&
                      Permit.canActions('rentQuality')
                    "
                    type="text"
                    size="mini"
                    @click="qualityItem(row)"
                    >{{ $t("action.quality") }}</el-button
                  >
                  <el-button
                    v-if="
                      ['processing', 'received', 'reworking'].includes(
                        row.workflowState
                      ) &&
                      row.canReworkQuantity != 0 &&
                      Permit.canActions('rentQuality')
                    "
                    type="text"
                    size="mini"
                    @click="reworkItem(row)"
                    >{{ $t("action.rework") }}</el-button
                  >
                  <el-button
                    v-if="
                      ['processing', 'reworking'].includes(row.workflowState) &&
                      row.canReceiveQuantity != 0 &&
                      Permit.canActions('receiveAndQuality')
                    "
                    type="text"
                    size="mini"
                    @click="receiveAndQualityItem(row)"
                    >{{ $t("action.receiveAndQuality") }}</el-button
                  >
                  <el-button
                    v-if="
                      row.workflowState == 'qualitied' &&
                      Permit.canActions('rentStockOut') &&
                      !(row.rent && row._rent.diffAmount < 0)
                    "
                    type="text"
                    size="mini"
                    @click="deliveryItem(row)"
                    >{{ $t("action.delivery") }}</el-button
                  >
                </template>

                <el-dropdown
                  v-if="!['canceled', 'returned'].includes(row.workflowState)"
                  class="mgl10"
                >
                  <a class="el-dropdown-link small-link">
                    {{ $t("text.more") }}
                    <i class="el-icon-arrow-down el-icon--right"></i>
                  </a>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item
                      v-if="
                        row.workflowState === 'newly' &&
                        Permit.canActions('rentManage')
                      "
                    >
                      <span @click="editItem(row, rowIndex)" class="font10">
                        {{ $t("text.edit") }}
                      </span>
                    </el-dropdown-item>
                    <el-dropdown-item
                      v-if="
                        row.workflowState === 'newly' &&
                        Permit.canActions('rentDelete')
                      "
                    >
                      <el-popconfirm
                        :title="$t('prompt.confirmDelete')"
                        @confirm="deleteItem(row)"
                      >
                        <span slot="reference" class="font10">
                          {{ $t("text.delete") }}
                        </span>
                      </el-popconfirm>
                    </el-dropdown-item>
                    <el-dropdown-item
                      v-if="
                        !['canceled', 'returned', 'finished'].includes(
                          row.workflowState
                        ) && Permit.canActions('rentCancel')
                      "
                    >
                      <span class="font10" @click="cancelItem(row)">
                        {{ $t("action.cancel") }}
                      </span>
                    </el-dropdown-item>
                    <el-dropdown-item
                      v-if="
                        row.workflowState == 'finished' &&
                        Permit.canActions('rentReturn')
                      "
                    >
                      <span class="font10" @click="returnItem(row)">
                        {{ $t("action.return") }}
                      </span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </span>
            </template>
          </template>
        </vxe-table-column>
      </vxe-table>
      <div class="flex flex-between pager">
        <div class="selected-qty-tips">
          选中了
          <span class="strong">{{ selectRecordQty }}</span> 条记录
        </div>
        <div>
          <vxe-pager
            size="small"
            :loading="false"
            :background="false"
            :current-page="pager.currentPage"
            :page-size="pager.pageSize"
            :total="pager.total"
            :layouts="pagerConfig.layouts"
            :page-sizes="pagerConfig.pageSizes"
            @page-change="changePager"
          ></vxe-pager>
        </div>
      </div>
    </div>

    <!-- ===== -->
    <drawer
      :title="$t('text.view')"
      :visible.sync="showRentDF"
      size="900px"
      direction="rtl"
      :wrapper-closable="false"
      :destroy-on-close="true"
    >
      <show
        v-if="row && ['rent', 'service'].includes(row.type)"
        :id="currentRowRentId"
        @updateInfo="showRentUpdated"
      />
      <show-clean v-if="row && row.type == 'clean'" :id="currentRowRentId" />
    </drawer>

    <drawer
      :title="$t('text.edit')"
      :visible.sync="editRentDF"
      size="900px"
      direction="rtl"
      :wrapper-closable="false"
      :destroy-on-close="true"
    >
      <edit
        v-if="row && ['rent', 'service'].includes(row.type)"
        :id="currentRowRentId"
        @updateInfo="rentUpdated"
      />
      <edit-clean
        v-if="row && row.type == 'clean'"
        :id="currentRowRentId"
        @updateInfo="rentUpdated"
      />
    </drawer>

    <drawer
      :title="$t('text.view')"
      :visible.sync="showItemDF"
      size="900px"
      direction="rtl"
      :wrapper-closable="false"
      :destroy-on-close="true"
    >
      <show-item-rent v-if="row && row.type == 'rent'" :id="currentRowItemId" />
      <show-item-service
        v-if="row && row.type == 'service'"
        :id="currentRowItemId"
      />
      <show-item-clean
        v-if="row && row.type == 'clean'"
        :id="currentRowItemId"
      />
      <show-item-order
        v-if="row && ['custom', 'available'].includes(row.type)"
        :id="currentRowItemId"
      />
    </drawer>

    <drawer
      :title="$t('text.edit')"
      :visible.sync="editRentItemDF"
      size="900px"
      direction="rtl"
      :wrapper-closable="false"
      :destroy-on-close="true"
    >
      <edit-item-rent
        v-if="row && row.type == 'rent'"
        :id="currentRowItemId"
        @updateInfo="itemUpdated"
      />
      <edit-item-service
        v-if="row && row.type == 'service'"
        :id="currentRowItemId"
        @updateInfo="itemUpdated"
      />
      <edit-item-clean
        v-if="row && row.type == 'clean'"
        :id="currentRowItemId"
        @updateInfo="itemUpdated"
      />
      <edit-item-order
        v-if="row && ['custom', 'available'].includes(row.type)"
        :id="currentRowItemId"
        @updateInfo="itemUpdated"
      />
    </drawer>

    <!-- 分配拥有人 -->
    <el-dialog
      :title="$t('action.distributiveOwner')"
      :close-on-click-modal="false"
      :visible.sync="distributiveOwnerDF"
      width="30%"
    >
      <el-form ref="distributiveOwnerObjForm" :model="distributiveOwnerObj">
        <el-form-item
          :prop="'ownerId'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('text.owner')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-select
            size="small"
            v-model="distributiveOwnerObj.ownerId"
            clearable
            filterable
          >
            <el-option
              v-for="user in usersFilterValid"
              :key="user.id"
              :label="user.name"
              :value="user.id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :loading="distributiveOwnerDoing"
          size="small"
          @click="distributiveOwnerSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="distributiveOwnerDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>

    <!-- 收款 -->
    <el-dialog
      :title="$t('action.addReceiveAndPayment')"
      :close-on-click-modal="false"
      :visible.sync="addPaymentDF"
      width="30%"
    >
      <el-form
        ref="addPaymentObjForm"
        :model="addPaymentObj"
        label-width="60px"
      >
        <el-row>
          <el-col :span="24" class="center">
            <el-radio-group v-model="addPaymentObj.type" size="small">
              <el-radio-button label="receivables">
                {{ $t("action.payment") }}
              </el-radio-button>
              <el-radio-button label="return">
                {{ $t("action.paymentReturn") }}
              </el-radio-button>
            </el-radio-group>
          </el-col>
        </el-row>
        <el-form-item
          :label="$t('payment.total')"
          :prop="'total'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('payment.total')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input size="small" v-model.trim="addPaymentObj.total"></el-input>
        </el-form-item>
        <el-form-item
          :label="$t('payment.payMethod')"
          :prop="'payMethodId'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('payment.payMethod')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-select
            size="small"
            v-model="addPaymentObj.payMethodId"
            clearable
            filterable
          >
            <el-option
              v-for="payMethod in payMethods"
              :key="payMethod.id"
              :label="payMethod.name"
              :value="payMethod.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          :label="$t('payment.paidAt')"
          :prop="'paidAt'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('payment.paidAt')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-date-picker
            size="small"
            v-model="addPaymentObj.paidAt"
            type="date"
            style="width: 100%"
            :picker-options="pickerOptionsFiveDays"
          ></el-date-picker>
        </el-form-item>
        <el-form-item :label="$t('payment.explain')" :prop="'explain'">
          <el-input
            size="small"
            v-model.trim="addPaymentObj.explain"
            type="textarea"
          />
        </el-form-item>
        <el-form-item :label="$t('payment.voucher')" :prop="'voucher'">
          <dropzone v-model="addPaymentObj.voucher" multiple />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :loading="addPaymentDoing"
          size="small"
          @click="addPaymentSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="addPaymentDF = false">
          {{ $t("text.close") }}
        </el-button>
      </div>
    </el-dialog>
    <!-- 收款押金 -->
    <el-dialog
      :title="$t('action.addDeposit')"
      :close-on-click-modal="false"
      :visible.sync="addDepositDF"
      width="30%"
    >
      <el-form
        ref="addDepositObjForm"
        :model="addDepositObj"
        label-width="60px"
      >
        <el-form-item
          :label="$t('payment.total')"
          :prop="'total'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('payment.total')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input size="small" v-model.trim="addDepositObj.total"></el-input>
        </el-form-item>
        <el-form-item
          :label="$t('payment.payMethod')"
          :prop="'payMethodId'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('payment.payMethod')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-select
            size="small"
            v-model="addDepositObj.payMethodId"
            clearable
            filterable
          >
            <el-option
              v-for="payMethod in payMethods"
              :key="payMethod.id"
              :label="payMethod.name"
              :value="payMethod.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          :label="$t('payment.paidAt')"
          :prop="'paidAt'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('payment.paidAt')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-date-picker
            size="small"
            v-model="addDepositObj.paidAt"
            type="date"
            style="width: 100%"
            :picker-options="pickerOptionsFiveDays"
          ></el-date-picker>
        </el-form-item>
        <el-form-item :label="$t('payment.explain')" :prop="'remark'">
          <el-input
            size="small"
            v-model.trim="addDepositObj.remark"
            type="textarea"
          />
        </el-form-item>
        <el-form-item :label="$t('payment.voucher')" :prop="'voucher'">
          <dropzone v-model="addDepositObj.voucher" multiple />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :loading="addDepositDoing"
          size="small"
          @click="addDepositSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="addDepositDF = false">
          {{ $t("text.close") }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 出库 -->
    <el-dialog
      :title="$t('rentItem.outRemark')"
      :close-on-click-modal="false"
      :visible.sync="outDF"
      width="30%"
    >
      <el-form ref="outObjForm" :model="outObj">
        <el-form-item :prop="'remark'">
          <el-input
            size="small"
            v-model.trim="outObj.remark"
            type="textarea"
            :rows="3"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :loading="outDoing"
          size="small"
          @click="outSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="outDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
    <!-- 入库/完成 -->
    <el-dialog
      :title="
        row && row.type == 'service'
          ? $t('rentItem.finishRemark')
          : $t('rentItem.inRemark')
      "
      :close-on-click-modal="false"
      :visible.sync="inDF"
      width="30%"
    >
      <el-form ref="inObjForm" :model="inObj">
        <el-form-item :prop="'remark'">
          <el-input
            size="small"
            v-model.trim="inObj.remark"
            type="textarea"
            :rows="3"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :loading="inDoing"
          size="small"
          @click="inSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="inDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>

    <!-- 取消 -->
    <el-dialog
      :title="$t('action.cancel')"
      :close-on-click-modal="false"
      :visible.sync="cancelItemDF"
      width="30%"
    >
      <el-form
        ref="cancelItemObjForm"
        :model="cancelItemObj"
        label-width="70px"
      >
        <el-form-item
          :label="$t('payment.total')"
          :prop="'total'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('payment.total')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input size="small" v-model.trim="cancelItemObj.total"></el-input>
        </el-form-item>
        <el-form-item
          :label="$t('payment.payMethod')"
          :prop="'payMethodId'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('payment.payMethod')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-select
            size="small"
            v-model="cancelItemObj.payMethodId"
            clearable
            filterable
          >
            <el-option
              v-for="payMethod in payMethods"
              :key="payMethod.id"
              :label="payMethod.name"
              :value="payMethod.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('rentItem.cancelReason')" :prop="'remark'">
          <el-input
            size="small"
            v-model.trim="cancelItemObj.remark"
            type="textarea"
          />
        </el-form-item>
        <el-form-item :label="$t('payment.voucher')" :prop="'voucher'">
          <dropzone v-model="cancelItemObj.voucher" multiple />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :loading="cancelItemDoing"
          size="small"
          @click="cancelItemSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="cancelItemDF = false">
          {{ $t("text.close") }}
        </el-button>
      </div>
    </el-dialog>
    <!-- 退货 -->
    <el-dialog
      :title="$t('action.return')"
      :close-on-click-modal="false"
      :visible.sync="returnItemDF"
      width="30%"
    >
      <el-form
        ref="returnItemObjForm"
        :model="returnItemObj"
        label-width="70px"
      >
        <el-form-item
          :label="$t('payment.total')"
          :prop="'total'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('payment.total')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input size="small" v-model.trim="returnItemObj.total"></el-input>
        </el-form-item>
        <el-form-item
          :label="$t('payment.payMethod')"
          :prop="'payMethodId'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('payment.payMethod')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-select
            size="small"
            v-model="returnItemObj.payMethodId"
            clearable
            filterable
          >
            <el-option
              v-for="payMethod in payMethods"
              :key="payMethod.id"
              :label="payMethod.name"
              :value="payMethod.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('rentItem.returnReason')" :prop="'remark'">
          <el-input
            size="small"
            v-model.trim="returnItemObj.remark"
            type="textarea"
          />
        </el-form-item>
        <el-form-item :label="$t('rentItem.hasGood')" :prop="'hasGood'">
          <el-checkbox v-model="returnItemObj.hasGood"> </el-checkbox>
        </el-form-item>
        <el-form-item :label="$t('payment.voucher')" :prop="'voucher'">
          <dropzone v-model="returnItemObj.voucher" multiple />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :loading="returnItemDoing"
          size="small"
          @click="returnItemSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="returnItemDF = false">
          {{ $t("text.close") }}
        </el-button>
      </div>
    </el-dialog>
    <!-- 备货 -->
    <el-dialog
      :title="$t('action.prepare')"
      :close-on-click-modal="false"
      :visible.sync="prepareItemDF"
      width="30%"
    >
      <el-form
        ref="availableItemObjForm"
        :model="prepareItemObj"
        label-width="70px"
      >
        <span v-if="invInfo == null">当前店铺不存在该产品</span>
        <span v-else>
          <div class="mgb10">
            {{ $t("inventory.quantity") }}：{{ invInfo.quantity }}
          </div>
          <div>{{ $t("inventory.totalLock") }}：{{ invInfo.totalLock }}</div>
        </span>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :loading="prepareItemDoing"
          size="small"
          @click="availableItemSubmit('preparing')"
          >{{ $t("orderStates.preparing") }}</el-button
        >
        <el-button
          type="success"
          :loading="prepareItemDoing"
          size="small"
          @click="availableItemSubmit('prepared')"
          >{{ $t("orderStates.prepared") }}</el-button
        >
        <el-button size="small" @click="prepareItemDF = false">
          {{ $t("text.close") }}
        </el-button>
      </div>
    </el-dialog>
    <!-- 发货 -->
    <el-dialog
      :title="$t('action.delivery')"
      :close-on-click-modal="false"
      :visible.sync="deliveryItemDF"
      width="30%"
    >
      <el-form
        ref="deliveryItemObjForm"
        :model="deliveryItemObj"
        label-width="80px"
      >
        <div class="center mgb10">
          <el-radio-group v-model="deliveryItemObj.deliverType" size="small">
            <el-radio-button
              :label="deliverType"
              v-for="deliverType of deliverTypes"
              :key="deliverType"
              >{{ $t("deliveryTypes." + deliverType) }}
            </el-radio-button>
          </el-radio-group>
        </div>
        <el-form-item
          v-if="deliveryItemObj.deliverType == 'express'"
          :label="$t('delivery.express')"
          :prop="'express'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('delivery.express')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            size="small"
            v-model.trim="deliveryItemObj.express"
          ></el-input>
        </el-form-item>
        <el-form-item
          v-if="deliveryItemObj.deliverType == 'express'"
          :label="$t('delivery.tracking')"
          :prop="'tracking'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('delivery.tracking')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            size="small"
            v-model.trim="deliveryItemObj.tracking"
          ></el-input>
        </el-form-item>
        <el-form-item
          v-if="deliveryItemObj.deliverType == 'carry'"
          :label="$t('delivery.sender')"
          :prop="'express'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('delivery.sender')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            size="small"
            v-model.trim="deliveryItemObj.express"
          ></el-input>
        </el-form-item>
        <el-form-item
          v-if="
            deliveryItemObj.deliverType == 'express' ||
            deliveryItemObj.deliverType == 'carry'
          "
          :label="$t('delivery.deliverCost')"
          :prop="'deliverCost'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('delivery.deliverCost')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            size="small"
            v-model.trim="deliveryItemObj.deliverCost"
          ></el-input>
        </el-form-item>
        <el-form-item :label="$t('text.remark')" :prop="'remark'">
          <el-input
            size="small"
            v-model.trim="deliveryItemObj.remark"
            type="textarea"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :loading="deliveryItemDoing"
          size="small"
          @click="deliveryItemSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="deliveryItemDF = false">
          {{ $t("text.close") }}
        </el-button>
      </div>
    </el-dialog>
    <!-- 收货 -->
    <el-dialog
      :title="$t('action.' + actionName)"
      :close-on-click-modal="false"
      :visible.sync="receiveItemDF"
      width="30%"
    >
      <el-form
        ref="receiveItemObjForm"
        :model="receiveItemObj"
        label-width="60px"
      >
        <el-form-item
          :label="$t('text.quantity')"
          :prop="'quantity'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('text.quantity')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            size="small"
            v-model.trim="receiveItemObj.quantity"
            :placeholder="maxQuantity"
          ></el-input>
        </el-form-item>
        <el-form-item :label="$t('text.remark')" :prop="'remark'">
          <el-input
            size="small"
            v-model.trim="receiveItemObj.remark"
            type="textarea"
          />
        </el-form-item>
        <el-form-item :label="$t('payment.voucher')" :prop="'voucher'">
          <dropzone v-model="receiveItemObj.voucher" multiple />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :loading="receiveItemDoing"
          size="small"
          @click="receiveItemSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="receiveItemDF = false">
          {{ $t("text.close") }}
        </el-button>
      </div>
    </el-dialog>

    <div id="printRentSlipDiv" style="display: none">
      <div style="width: 100%">
        <div style="text-align: center; font-size: 18px; margin-bottom: 5px">
          {{ Permit.currentTenant().name }}租赁明细
        </div>
        <div style="text-align: center; font-size: 14px; margin-bottom: 5px">
          {{ printRent.shopName }}&nbsp;&nbsp;{{ printRent.ownerName }}
        </div>
        <table
          style="
            width: 100%;
            border-collapse: collapse;
            border: 1px solid #dfe5ec;
            text-align: left;
            margin-bottom: 20px;
            padding: 5px;
          "
        >
          <tr
            style="
              border: 1px solid #dfe5ec;
              text-align: left;
              padding-left: 5px;
            "
          >
            <td style="border: 1px solid #dfe5ec; padding: 5px" colspan="15">
              {{ $t("text.serialNo") }}：{{ printRent.serialNo }}
              <span style="float-right mgl10" v-if="printRent.type == 'rent'">
                {{ $t("customer.name") }}：{{ printRent.customerName }}
                <small>{{ printRent.customerPhone }}</small>
                <span v-if="printRent.customerWeddingDay != null">
                  {{ $t("customer.weddingDay") }}：{{
                    printRent.customerWeddingDay
                  }}
                </span>
                &nbsp;&nbsp;&nbsp;&nbsp;
                {{ $t("tenant.address") }}：{{
                  printRent.customerProvince || ""
                }}
                {{ printRent.customerCity || "" }}
                {{ printRent.customerAddress || "" }}
              </span>
            </td>
          </tr>
          <tr style="border: 1px solid #dfe5ec; text-align: center">
            <td style="border: 1px solid #dfe5ec; width: 30px; padding: 5px">
              #
            </td>
            <td style="border: 1px solid #dfe5ec; width: 50px">
              {{ $t("product.images") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 120px">
              {{ $t("text.serialNo") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 100px">
              {{ $t("product.productNo") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.category") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.brand") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.color") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.size") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("text.quantity") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 60px">
              {{ $t("product.salesPrice") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 60px">
              {{ $t("text.price2") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 80px">
              {{ $t("text.subtotal") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 80px">
              {{ $t("rent.rentAt") }}起
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 80px">
              {{ $t("rent.rentAt") }}止
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 80px">
              {{ $t("text.remark") }}
            </td>
          </tr>
          <tr
            v-for="(item, itemIndex) in printRent.items"
            :key="itemIndex"
            style="border: 1px solid #dfe5ec; text-align: center"
          >
            <td style="border: 1px solid #dfe5ec">
              {{ itemIndex + 1 }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              <img
                v-if="
                  item.images != null &&
                  item.images != '' &&
                  item.images.split(';').length != 0
                "
                :src="item.images.split(';')[0]"
                style="width: 50px"
              />
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.serialNo }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.productNo }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.categoryName }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.brand }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.color }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.size }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.quantity }}{{ item.unit }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              <span v-if="printRent.type != 'clean'">
                {{ item.productPrice | currency }}
              </span>
            </td>
            <td style="border: 1px solid #dfe5ec">
              <span v-if="printRent.type != 'clean'">
                {{ item.price | currency }}
              </span>
            </td>
            <td style="border: 1px solid #dfe5ec">
              <span v-if="printRent.type != 'clean'">
                {{ (item.quantity * item.price) | currency }}
              </span>
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.startAt }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.endAt }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              <raw-html
                :content="item.remark"
                style="font-size: 10px"
              ></raw-html>
            </td>
          </tr>
          <tr
            style="border: 1px solid #dfe5ec; text-align: left"
            v-if="printRent.type == 'rent'"
          >
            <td
              style="border: 1px solid #dfe5ec; padding-left: 5px"
              colspan="2"
            >
              {{ $t("rent.receivePaymentInfo") }}
            </td>
            <td style="border: 1px solid #dfe5ec" colspan="13">
              {{ $t("rent.total") }}：{{
                printRent.amount | currency
              }}&nbsp;&nbsp; {{ $t("rent.actualReceive") }}：{{
                printRent.payAmount | currency
              }}&nbsp;&nbsp;
              <span v-if="printRent.diffAmount < 0">
                {{ $t("payment.differReceive") }}：{{
                  -printRent.diffAmount | currency
                }}&nbsp;&nbsp;
              </span>
              <span v-if="printRent.diffAmount > 0">
                {{ $t("payment.leavePayAmount") }}：{{
                  printRent.diffAmount | currency
                }}&nbsp;&nbsp;
              </span>
            </td>
          </tr>
          <tr style="border: 1px solid #dfe5ec; text-align: left">
            <td
              style="border: 1px solid #dfe5ec; padding-left: 5px"
              colspan="2"
            >
              {{ $t("text.remark") }}
            </td>
            <td style="border: 1px solid #dfe5ec" colspan="13">
              <raw-html :content="printRent.remark"></raw-html>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <div id="printRentSlipDiv2" style="display: none">
      <div style="width: 100%">
        <div style="text-align: center; font-size: 18px; margin-bottom: 5px">
          {{ Permit.currentTenant().name }}租赁明细
        </div>
        <div style="text-align: center; font-size: 14px; margin-bottom: 5px">
          {{ printRent.shopName }}&nbsp;&nbsp;{{ printRent.ownerName }}
        </div>
        <table
          style="
            width: 100%;
            border-collapse: collapse;
            border: 1px solid #dfe5ec;
            text-align: left;
            margin-bottom: 20px;
            padding: 5px;
          "
        >
          <tr
            style="
              border: 1px solid #dfe5ec;
              text-align: left;
              padding-left: 5px;
            "
          >
            <td style="border: 1px solid #dfe5ec; padding: 5px" colspan="14">
              {{ $t("text.serialNo") }}：{{ printRent.serialNo }}
              <span style="float-right mgl10" v-if="printRent.type == 'rent'">
                {{ $t("customer.name") }}：{{ printRent.customerName }}
                <small>{{ printRent.customerPhone }}</small>
                <span v-if="printRent.customerWeddingDay != null">
                  {{ $t("customer.weddingDay") }}：{{
                    printRent.customerWeddingDay
                  }}
                </span>
                &nbsp;&nbsp;&nbsp;&nbsp;
                {{ $t("tenant.address") }}：{{
                  printRent.customerProvince || ""
                }}
                {{ printRent.customerCity || "" }}
                {{ printRent.customerAddress || "" }}
              </span>
            </td>
          </tr>
          <tr style="border: 1px solid #dfe5ec; text-align: center">
            <td style="border: 1px solid #dfe5ec; width: 30px; padding: 5px">
              #
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 120px">
              {{ $t("text.serialNo") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 100px">
              {{ $t("product.productNo") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.category") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.brand") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.color") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.size") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("text.quantity") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 60px">
              {{ $t("product.salesPrice") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 60px">
              {{ $t("text.price2") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 80px">
              {{ $t("text.subtotal") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 80px">
              {{ $t("rent.rentAt") }}起
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 80px">
              {{ $t("rent.rentAt") }}止
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 80px">
              {{ $t("text.remark") }}
            </td>
          </tr>
          <tr
            v-for="(item, itemIndex) in printRent.items"
            :key="itemIndex"
            style="border: 1px solid #dfe5ec; text-align: center"
          >
            <td style="border: 1px solid #dfe5ec">
              {{ itemIndex + 1 }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.serialNo }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.productNo }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.categoryName }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.brand }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.color }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.size }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.quantity }}{{ item.unit }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              <span v-if="printRent.type != 'clean'">
                {{ item.productPrice | currency }}
              </span>
            </td>
            <td style="border: 1px solid #dfe5ec">
              <span v-if="printRent.type != 'clean'">
                {{ item.price | currency }}
              </span>
            </td>
            <td style="border: 1px solid #dfe5ec">
              <span v-if="printRent.type != 'clean'">
                {{ (item.quantity * item.price) | currency }}
              </span>
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.startAt }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              {{ item.endAt }}
            </td>
            <td style="border: 1px solid #dfe5ec">
              <raw-html
                :content="item.remark"
                style="font-size: 10px"
              ></raw-html>
            </td>
          </tr>
          <tr
            style="border: 1px solid #dfe5ec; text-align: left"
            v-if="printRent.type == 'rent'"
          >
            <td
              style="border: 1px solid #dfe5ec; padding-left: 5px"
              colspan="2"
            >
              {{ $t("rent.receivePaymentInfo") }}
            </td>
            <td style="border: 1px solid #dfe5ec" colspan="12">
              {{ $t("rent.total") }}：{{
                printRent.amount | currency
              }}&nbsp;&nbsp; {{ $t("rent.actualReceive") }}：{{
                printRent.payAmount | currency
              }}&nbsp;&nbsp;
              <span v-if="printRent.diffAmount < 0">
                {{ $t("payment.differReceive") }}：{{
                  -printRent.diffAmount | currency
                }}&nbsp;&nbsp;
              </span>
              <span v-if="printRent.diffAmount > 0">
                {{ $t("payment.leavePayAmount") }}：{{
                  printRent.diffAmount | currency
                }}&nbsp;&nbsp;
              </span>
            </td>
          </tr>
          <tr style="border: 1px solid #dfe5ec; text-align: left">
            <td
              style="border: 1px solid #dfe5ec; padding-left: 5px"
              colspan="2"
            >
              {{ $t("text.remark") }}
            </td>
            <td style="border: 1px solid #dfe5ec" colspan="12">
              <raw-html :content="printRent.remark"></raw-html>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <div id="printRentSlipDiv3" style="display: none">
      <div style="width: 100%">
        <div style="text-align: center; font-size: 18px; margin-bottom: 5px">
          {{ Permit.currentTenant().name }}租赁明细
        </div>
        <div style="text-align: center; font-size: 14px; margin-bottom: 5px">
          {{ printRent.shopName }}&nbsp;&nbsp;{{ printRent.ownerName }}
        </div>
        <table
          style="
            width: 100%;
            border-collapse: collapse;
            border: 1px solid #dfe5ec;
            text-align: left;
            margin-bottom: 20px;
            padding: 5px;
          "
        >
          <tr
            style="
              border: 1px solid #dfe5ec;
              text-align: left;
              padding-left: 5px;
            "
          >
            <td style="border: 1px solid #dfe5ec; padding: 5px" colspan="13">
              {{ $t("text.serialNo") }}：{{ printRent.serialNo }}
              <span style="float-right mgl10" v-if="printRent.type == 'rent'">
                {{ $t("customer.name") }}：{{ printRent.customerName }}
                <small>{{ printRent.customerPhone }}</small>
                <span v-if="printRent.customerWeddingDay != null">
                  {{ $t("customer.weddingDay") }}：{{
                    printRent.customerWeddingDay
                  }}
                </span>
                &nbsp;&nbsp;&nbsp;&nbsp;
                {{ $t("tenant.address") }}：{{
                  printRent.customerProvince || ""
                }}
                {{ printRent.customerCity || "" }}
                {{ printRent.customerAddress || "" }}
              </span>
            </td>
          </tr>
          <tr style="border: 1px solid #dfe5ec; text-align: center">
            <td style="border: 1px solid #dfe5ec; width: 30px; padding: 5px">
              #
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 120px">
              {{ $t("text.serialNo") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 100px">
              {{ $t("product.productNo") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.category") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.brand") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.color") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("product.size") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 50px">
              {{ $t("text.quantity") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 60px">
              {{ $t("product.salesPrice") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 60px">
              {{ $t("text.price2") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 80px">
              {{ $t("text.subtotal") }}
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 80px">
              {{ $t("rent.rentAt") }}起
            </td>
            <td style="border: 1px solid #dfe5ec; min-width: 80px">
              {{ $t("rent.rentAt") }}止
            </td>
          </tr>
          <span v-for="(item, itemIndex) in printRent.items" :key="itemIndex">
            <tr style="border: 1px solid #dfe5ec; text-align: center">
              <td style="border: 1px solid #dfe5ec" rowspan="2">
                {{ itemIndex + 1 }}
              </td>
              <td style="border: 1px solid #dfe5ec">
                {{ item.serialNo }}
              </td>
              <td style="border: 1px solid #dfe5ec">
                {{ item.productNo }}
              </td>
              <td style="border: 1px solid #dfe5ec">
                {{ item.categoryName }}
              </td>
              <td style="border: 1px solid #dfe5ec">
                {{ item.brand }}
              </td>
              <td style="border: 1px solid #dfe5ec">
                {{ item.color }}
              </td>
              <td style="border: 1px solid #dfe5ec">
                {{ item.size }}
              </td>
              <td style="border: 1px solid #dfe5ec">
                {{ item.quantity }}{{ item.unit }}
              </td>
              <td style="border: 1px solid #dfe5ec">
                <span v-if="printRent.type != 'clean'">
                  {{ item.productPrice | currency }}
                </span>
              </td>
              <td style="border: 1px solid #dfe5ec">
                <span v-if="printRent.type != 'clean'">
                  {{ item.price | currency }}
                </span>
              </td>
              <td style="border: 1px solid #dfe5ec">
                <span v-if="printRent.type != 'clean'">
                  {{ (item.quantity * item.price) | currency }}
                </span>
              </td>
              <td style="border: 1px solid #dfe5ec">
                {{ item.startAt }}
              </td>
              <td style="border: 1px solid #dfe5ec">
                {{ item.endAt }}
              </td>
            </tr>
            <tr style="border: 1px solid #dfe5ec">
              <td style="border: 1px solid #dfe5ec; text-align: center">
                {{ $t("text.remark") }}
              </td>
              <td style="border: 1px solid #dfe5ec" colspan="10">
                <raw-html
                  :content="item.remark"
                  style="font-size: 10px"
                ></raw-html>
              </td>
            </tr>
          </span>

          <tr
            style="border: 1px solid #dfe5ec; text-align: left"
            v-if="printRent.type == 'rent'"
          >
            <td
              style="border: 1px solid #dfe5ec; padding-left: 5px"
              colspan="2"
            >
              {{ $t("rent.receivePaymentInfo") }}
            </td>
            <td style="border: 1px solid #dfe5ec" colspan="10">
              {{ $t("rent.total") }}：{{
                printRent.amount | currency
              }}&nbsp;&nbsp; {{ $t("rent.actualReceive") }}：{{
                printRent.payAmount | currency
              }}&nbsp;&nbsp;
              <span v-if="printRent.diffAmount < 0">
                {{ $t("payment.differReceive") }}：{{
                  -printRent.diffAmount | currency
                }}&nbsp;&nbsp;
              </span>
              <span v-if="printRent.diffAmount > 0">
                {{ $t("payment.leavePayAmount") }}：{{
                  printRent.diffAmount | currency
                }}&nbsp;&nbsp;
              </span>
            </td>
          </tr>
          <tr style="border: 1px solid #dfe5ec; text-align: left">
            <td
              style="border: 1px solid #dfe5ec; padding-left: 5px"
              colspan="2"
            >
              {{ $t("text.remark") }}
            </td>
            <td style="border: 1px solid #dfe5ec" colspan="10">
              <raw-html :content="printRent.remark"></raw-html>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <!-- 打印自定义租赁单 -->
    <div id="printRentSlipCustomDiv" style="display: none">
      <div id="customPrintContentRentSlip" />
    </div>
  </div>
</template>
<script>
import Vue from "vue";
import axios from "axios";
import { table, page } from "@/utils/mixins.js";
import Util from "@/utils/index.js";
import DateRange from "@/components/DateRange";
import RawHtmlLimitNumber from "@/components/RawHtmlLimitNumber";
import RawHtml from "@/components/RawHtml";
import Dropzone from "@/components/Dropzone";
import ThumbnailText from "@/components/ThumbnailText";

import Show from "./show";
import Edit from "./edit";

import ShowItemRent from "./showItem";
import EditItemRent from "./editItem";
//
import ShowItemService from "../services/showItem";
import editItemService from "../services/editItem";
//
import ShowItemClean from "../cleans/showItem";
import EditItemClean from "../cleans/editItem";
//
import ShowItemOrder from "../orders/showItem";
import EditItemOrder from "../orders/editItem";
//
import ShowClean from "../cleans/show";
import EditClean from "../cleans/edit";

//
import Rent from "@/api/rent.js";
import RentItem from "@/api/rentItem.js";
import Order from "@/api/order.js";
import Shop from "@/api/shop.js";
import User from "@/api/user.js";
import PayMethod from "@/api/payMethod.js";
import Payment from "@/api/payment.js";
import RentPackage from "@/api/rentPackage.js";
import UserAuth from "@/api/userAuth.js";
import PrintTemplate from "@/api/printTemplate.js";
import Inventory from "@/api/inventory.js";
import Delivery from "@/api/delivery.js";
import Receive from "@/api/receive.js";
import Quality from "@/api/quality.js";
import Rework from "@/api/rework.js";

export default {
  mixins: [
    page,
    table.pager,
    table.narrow,
    table.search,
    table.checker,
    table.sorter,
    table.basic,
  ],
  components: {
    DateRange,
    RawHtmlLimitNumber,
    RawHtml,
    Dropzone,
    ThumbnailText,

    Show,
    Edit,
    ShowClean,
    EditClean,

    ShowItemRent,
    EditItemRent,

    ShowItemService,
    editItemService,

    ShowItemOrder,
    EditItemOrder,

    ShowItemClean,
    EditItemClean,
  },

  data() {
    return {
      Util: Util,
      sorter: {
        sortBy: "rent_id",
        sortIndicator: "desc",
      },
      narrows: [
        [
          {
            label: this.$t("text.workflowState"),
            items: RentItem.states_rent_order.map((s) => {
              return {
                key: s,
                label: this.$t("rentOrderStates." + s),
                condition: { workflowState: s },
              };
            }),
          },
        ],
        [
          {
            label: this.$t("text.type"),
            items: RentItem.types.map((t) => {
              return {
                key: t,
                label: this.$t("itemTypes." + t),
                condition: { type: t },
              };
            }),
          },
          {
            label: this.$t("text.special"),
            items: [
              {
                label: this.$t("payment.debt"),
                key: "debt",
                condition: { special: "debt" },
              },
              {
                label: this.$t("payment.depositDebt"),
                key: "depositDebt",
                condition: { special: "depositDebt" },
              },
            ],
          },
        ],
      ],
      initFilter: null,
      searchForm: {
        keyword: null,
        productNo: null,
        rentAtStartAt: null,
        rentAtEndAt: null,
        createAtStartAt: null,
        createAtEndAt: null,
        shopId: null,
      },
      shops: [],
      //
      row: null,
      currentRowIdx: null,
      currentRowRentId: null,
      currentRowItemId: null,

      itemReceives: [],
      itemQualities: [],
      itemReworks: [],

      showRentDF: false,
      editRentDF: false,
      showItemDF: false,
      editRentItemDF: false,
      // 分配拥有人
      distributiveOwnerDF: false,
      distributiveOwnerObj: {
        ownerId: null,
        ownerName: null,
      },
      usersFilterValid: [],
      distributiveOwnerDoing: false,
      // 收款
      addPaymentDF: false,
      pickerOptionsFiveDays: {
        disabledDate(time) {
          const curDate = new Date().getTime();
          const day = 5 * 24 * 3600 * 1000;
          const threeMonths = curDate - day;
          return time.getTime() > Date.now() || time.getTime() < threeMonths;
        },
      },
      payMethods: [],
      customer: null,
      addPaymentObj: {
        type: "receivables", // return
        total: null,
        payMethodId: null,
        payMethodName: null,
        explain: null,
        rentId: null,
        paidAt: null,
        voucher: null,
        settlementId: 0, // 先付款
        customerId: null,
      },
      addPaymentDoing: false,
      //
      addDepositDF: false,
      addDepositObj: {
        total: null,
        payMethodId: null,
        payMethodName: null,
        paidAt: null,
        remark: null,
        voucher: null,
      },
      addDepositDoing: false,
      //
      printTemplatesRent: [],
      printRent: { shopName: null },
      customPrintContent: "",
      // 出库
      outDF: false,
      outObj: {
        remark: null,
      },
      outDoing: false,
      // 入库
      inDF: false,
      inObj: {
        remark: null,
      },
      inDoing: false,
      //取消
      cancelItemDF: false,
      cancelItemObj: {
        total: null,
        payMethodId: null,
        payMethodName: null,
        remark: null,
        voucher: null,
      },
      cancelItemDoing: false,
      //退货
      returnItemDF: false,
      returnItemObj: {
        total: null,
        payMethodId: null,
        payMethodName: null,
        remark: null,
        voucher: null,
        hasGood: true,
      },
      returnItemDoing: false,
      //备货
      prepareItemDF: false,
      invInfo: null,
      prepareItemObj: {},
      prepareItemDoing: false,
      //发货
      deliverTypes: Delivery.deliverTypes,
      deliveryItemDF: false,
      deliveryItemObj: {
        deliverType: null,
        express: null,
        tracking: null,
        deliverCost: null,
        remark: null,
      },
      deliveryItemDoing: false,
      //收货
      receiveItemDF: false,
      actionName: "receive",
      maxQuantity: null,
      receiveItemObj: {
        quantity: null,
        remark: null,
        voucher: null,
      },
      receiveItemDoing: false,
    };
  },
  created() {
    const self = this;
    this.$nextTick(() => {
      // 手动将表格和工具栏进行关联
      this.$refs.recordsTable.connect(this.$refs.recordsToolbar);
    });
    axios
      .all([
        User.findByTenantId(self.Permit.currentTenant().id),
        Shop.findByTenantId(),
        RentPackage.findByTenantId(),
        PrintTemplate.findByType({ type: "rent" }),
      ])
      .then(
        axios.spread(function (users, shops, rentPackages, printTemplatesRent) {
          if (users.status === 200) {
            self.users = users.data.filter((u) => u.authorizeW === true);
          } else {
            self.sysError();
          }
          if (shops.status === 200) {
            self.shops = shops.data;
          } else {
            self.sysError();
          }
          if (rentPackages.status === 200) {
            if (rentPackages.data.length != 0) {
              self.narrows.push([
                {
                  label: self.$t("menu.rentPackages"),
                  items: rentPackages.data.map((rp) => {
                    return {
                      label: rp.name,
                      key: rp.id,
                      condition: { rentPackageId: rp.id },
                    };
                  }),
                },
              ]);
            }
          } else {
            self.sysError();
          }
          if (printTemplatesRent.status === 200) {
            self.printTemplatesRent = printTemplatesRent.data;
          } else {
            self.sysError();
          }
        })
      );
  },
  methods: {
    checkTenantValid(routerName) {
      const self = this;
      UserAuth.checkTenantValid().then(function (res) {
        if (res.status == 200) {
          if (res.data.type == "fail") {
            self.sysResultCheckTenantValid(res.data);
            return;
          }
          self.$router.push({ name: routerName });
        } else {
          self.sysError();
        }
      });
    },
    create(routerName) {
      const self = this;
      self.$router.push({ name: routerName });
    },

    getItemReceives(row) {
      const self = this;
      Receive.findByOrderId(row.id).then(function (res) {
        if (res.status == 200) {
          self.itemReceives = res.data;
        } else {
          self.sysError();
        }
      });
    },
    getItemQualities(row) {
      const self = this;
      Quality.findByOrderId(row.id).then(function (res) {
        if (res.status == 200) {
          self.itemQualities = res.data;
        } else {
          self.sysError();
        }
      });
    },
    getItemReworks(row) {
      const self = this;
      Rework.findByOrderId(row.id).then(function (res) {
        if (res.status == 200) {
          self.itemReworks = res.data;
        } else {
          self.sysError();
        }
      });
    },

    // 下载
    download(type) {
      const self = this;
      if (type === "search") {
        if (self.pager.total === 0 || self.pager.total > 100000) {
          self.$message.error(this.$t("prompt.downloadDataRange100000"));
          return;
        }
        const data = JSON.parse(JSON.stringify(self.combineParams()));
        data.pageSize = 100000;
        Vue.set(data, "excelType", "general");
        self.downloadData(data);
      }
      if (type == "checked") {
        if (
          self.selectedBargains().length === 0 ||
          self.selectedBargains().length > 100000
        ) {
          self.$message.error(self.$t("prompt.downloadDataRange100000"));
          return;
        }
        const data = {
          currentPage: 1,
          pageSize: 100000,
          ids: self.selectedBargains().map((c) => c.id),
          excelType: "general",
        };
        self.downloadData(data);
      }
    },
    downloadData(data) {
      const self = this;
      Rent.exportExcel(data).then(function (res) {
        if (res.data.code === 200) {
          self.$message({
            message: self.$t("prompt.downloadPrompt"),
            type: "success",
          });
          self.$eventHub.$emit("emitDownLoad");
        } else {
          self.$message.error(self.$t("prompt.downloadErr"));
        }
      });
    },

    prepareItemRent(row) {
      const self = this;
      self.prepareItemRentDoing = true;
      RentItem.preparing(row.id, {}).then(function (res) {
        if (res.status === 200) {
          self.sysResult(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
        self.prepareItemRentDoing = false;
      });
    },
    preparedItemRent(row) {
      const self = this;
      self.preparedItemRentDoing = true;
      RentItem.prepared(row.id, {}).then(function (res) {
        if (res.status === 200) {
          self.sysResult(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
        self.preparedItemRentDoing = false;
      });
    },

    //收货
    receiveItem(row) {
      const self = this;
      self.row = row;
      self.actionName = "receive";
      self.maxQuantity = row.canReceiveQuantity;
      self.receiveItemObj.quantity = row.canReceiveQuantity;
      self.setReceiveItemDF();
    },
    qualityItem(row) {
      const self = this;
      self.row = row;
      self.actionName = "quality";
      self.maxQuantity = row.canQualityQuantity;
      self.receiveItemObj.quantity = row.canQualityQuantity;
      self.setReceiveItemDF();
    },
    reworkItem(row) {
      const self = this;
      self.row = row;
      self.actionName = "rework";
      self.maxQuantity = row.canReworkQuantity;
      self.receiveItemObj.quantity = row.canReworkQuantity;
      self.setReceiveItemDF();
    },
    receiveAndQualityItem(row) {
      const self = this;
      self.row = row;
      self.actionName = "receiveAndQuality";
      self.maxQuantity = row.canReceiveQuantity;
      self.receiveItemObj.quantity = row.canReceiveQuantity;
      self.setReceiveItemDF();
    },
    setReceiveItemDF() {
      const self = this;
      self.receiveItemObj.remark = null;
      self.receiveItemObj.voucher = null;
      self.receiveItemDoing = false;
      self.receiveItemDF = true;
    },

    receiveItemSubmit() {
      const self = this;
      self.$refs.receiveItemObjForm.validate((valid) => {
        if (valid) {
          if (self.actionName == "receive") {
            self.receiveItemFun();
          }
          if (self.actionName == "quality") {
            self.qualityItemFun();
          }
          if (self.actionName == "quality") {
            self.qualityItemFun();
          }
          if (self.actionName == "rework") {
            self.reworkItemFun();
          }
          if (self.actionName == "receiveAndQuality") {
            self.receiveAndQualityItemFun();
          }
        } else {
          return false;
        }
      });
    },
    receiveItemFun() {
      const self = this;
      self.receiveItemDoing = true;
      Order.receive(self.row.id, self.receiveItemObj).then(function (res) {
        if (res.status === 200) {
          self.sysResult(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
        self.receiveItemDoing = false;
        self.receiveItemDF = false;
      });
    },
    qualityItemFun() {
      const self = this;
      self.receiveItemDoing = true;
      Order.quality(self.row.id, self.receiveItemObj).then(function (res) {
        if (res.status === 200) {
          self.sysResult(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
        self.receiveItemDoing = false;
        self.receiveItemDF = false;
      });
    },
    reworkItemFun() {
      const self = this;
      self.receiveItemDoing = true;
      Order.rework(self.row.id, self.receiveItemObj).then(function (res) {
        if (res.status === 200) {
          self.sysResult(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
        self.receiveItemDoing = false;
        self.receiveItemDF = false;
      });
    },
    receiveAndQualityItemFun() {
      const self = this;
      self.receiveItemDoing = true;
      Order.receiveAndQuality(self.row.id, self.receiveItemObj).then(function (
        res
      ) {
        if (res.status === 200) {
          self.sysResult(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
        self.receiveItemDoing = false;
        self.receiveItemDF = false;
      });
    },

    // 去生产
    processingItem(row) {
      const self = this;
      Order.processing(row.id).then(function (res) {
        if (res.status == 200) {
          self.sysResult(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
      });
    },
    deliveryAll(row) {
      const self = this;
      self.row = row;
      self.actionName = "deliveryAll";
      self.deliveryItemObj.deliverType = "take";
      self.deliveryItemObj.express = null;
      self.deliveryItemObj.tracking = null;
      self.deliveryItemObj.deliverCost = null;
      self.deliveryItemObj.remark = null;
      self.deliveryItemDoing = false;
      self.deliveryItemDF = true;
    },
    // 发货
    deliveryItem(row) {
      const self = this;
      self.row = row;
      self.actionName = "deliveryItem";
      self.deliveryItemObj.deliverType = "take";
      self.deliveryItemObj.express = null;
      self.deliveryItemObj.tracking = null;
      self.deliveryItemObj.deliverCost = null;
      self.deliveryItemObj.remark = null;
      self.deliveryItemDoing = false;
      self.deliveryItemDF = true;
    },
    deliveryItemSubmit() {
      const self = this;
      self.$refs.deliveryItemObjForm.validate((valid) => {
        if (valid) {
          if (self.actionName == "deliveryAll") {
            self.rentDeliveryFun();
          }
          if (self.actionName == "deliveryItem") {
            if (self.row.type == "rent") {
              self.rentItemDeliveryFun();
            } else {
              self.orderDeliveryFun();
            }
          }
        } else {
          return false;
        }
      });
    },
    rentDeliveryFun() {
      const self = this;
      self.deliveryItemDoing = true;
      Rent.delivery(self.row.id, self.deliveryItemObj).then(function (res) {
        if (res.status === 200) {
          self.sysResults(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
        self.deliveryItemDoing = false;
        self.deliveryItemDF = false;
      });
    },
    rentItemDeliveryFun() {
      const self = this;
      self.deliveryItemDoing = true;
      RentItem.delivery(self.row.id, self.deliveryItemObj).then(function (res) {
        if (res.status === 200) {
          self.sysResult(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
        self.deliveryItemDoing = false;
        self.deliveryItemDF = false;
      });
    },
    orderDeliveryFun() {
      const self = this;
      self.deliveryItemDoing = true;
      Order.delivery(self.row.id, self.deliveryItemObj).then(function (res) {
        if (res.status === 200) {
          self.sysResult(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
        self.deliveryItemDoing = false;
        self.deliveryItemDF = false;
      });
    },

    //现货交付
    availableDeliveryItem(row) {
      const self = this;
      Order.spotDelivery(row.id).then(function (res) {
        if (res.status == 200) {
          self.sysResult(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
      });
    },

    //备货完毕
    preparedItem(row) {
      const self = this;
      self.preparedFun(row);
    },
    //备货
    prepareItem(row) {
      const self = this;
      self.row = row;
      self.invInfo = null;
      let obj = {
        productNo: row.productNo,
        color: row.color,
        size: row.size,
        shopId: row._rent.shopId,
      };
      Inventory.findByProductInfo(obj).then(function (res) {
        if (res.data.code == 200) {
          self.invInfo = res.data.entity;
          self.prepareItemDoing = false;
          self.prepareItemDF = true;
        } else {
          self.$message({
            message: "现货库存不存在",
            type: "warning",
          });
          self.sysError();
        }
      });
    },
    availableItemSubmit(type) {
      const self = this;
      if (type == "preparing") {
        self.preparingFun(self.row);
      }
      if (type == "prepared") {
        self.preparedFun(self.row);
      }
    },
    preparingFun(row) {
      const self = this;
      Order.preparing(row.id).then(function (res) {
        if (res.status == 200) {
          self.sysResult(res.data);
          self.loadData();
          self.prepareItemDF = false;
        } else {
          self.sysError();
        }
      });
    },
    preparedFun(row) {
      const self = this;
      Order.prepared(row.id).then(function (res) {
        if (res.status == 200) {
          self.sysResult(res.data);
          self.loadData();
          self.prepareItemDF = false;
        } else {
          self.sysError();
        }
      });
    },
    //退货
    returnItem(row) {
      const self = this;
      self.row = row;
      self.returnItemObj.remark = null;
      self.returnItemObj.payMethodId = null;
      self.returnItemObj.payMethodName = null;
      self.returnItemObj.total = row.quantity * row.price;
      self.returnItemObj.voucher = null;
      self.returnItemObj.hasGood = true;
      self.getPayMethods();
      self.returnItemDoing = false;
      self.returnItemDF = true;
    },
    returnItemSubmit() {
      const self = this;
      self.$refs.returnItemObjForm.validate((valid) => {
        if (valid) {
          if (!Util.regexQuantity(self.returnItemObj.total)) {
            self.$message({
              message: self.$t("rules.regex", [self.$t("payment.total")]),
              type: "warning",
            });
            return;
          }
          self.returnItemObj.payMethodName = self.payMethods.find(
            (p) => p.id === self.returnItemObj.payMethodId
          ).name;
          self.cancelItemDoing = true;
          Order.return(self.row.id, self.returnItemObj).then(function (res) {
            if (res.status === 200) {
              self.$message({
                message: self.$t("prompt.returnSuccess"),
                type: "success",
              });
              self.loadData();
            } else {
              self.sysError();
            }
            self.returnItemDoing = false;
            self.returnItemDF = false;
          });
        } else {
          return false;
        }
      });
    },
    //取消
    cancelItem(row) {
      const self = this;
      self.row = row;
      self.cancelItemObj.remark = null;
      self.cancelItemObj.payMethodId = null;
      self.cancelItemObj.payMethodName = null;
      self.cancelItemObj.total = row.quantity * row.price;
      self.cancelItemObj.voucher = null;
      self.getPayMethods();
      self.cancelItemDoing = false;
      self.cancelItemDF = true;
    },
    getPayMethods() {
      const self = this;
      PayMethod.findByTenantId().then(function (res) {
        if (res.status === 200) {
          self.payMethods = res.data;
        } else {
          self.sysError();
        }
      });
    },
    cancelItemSubmit() {
      const self = this;
      self.$refs.cancelItemObjForm.validate((valid) => {
        if (valid) {
          if (!Util.regexQuantity(self.cancelItemObj.total)) {
            self.$message({
              message: self.$t("rules.regex", [self.$t("payment.total")]),
              type: "warning",
            });
            return;
          }
          self.cancelItemObj.payMethodName = self.payMethods.find(
            (p) => p.id === self.cancelItemObj.payMethodId
          ).name;
          if (["custom", "available"].includes(self.row.type)) {
            self.orderCancel();
          } else {
            self.rentItemCancel();
          }
        } else {
          return false;
        }
      });
    },
    orderCancel() {
      const self = this;
      self.cancelItemDoing = true;
      Order.cancel(self.row.id, self.cancelItemObj).then(function (res) {
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.cancelSuccess"),
            type: "success",
          });
          self.loadData();
        } else {
          self.sysError();
        }
        self.cancelItemDoing = false;
        self.cancelItemDF = false;
      });
    },
    rentItemCancel() {
      const self = this;
      self.cancelItemDoing = true;
      RentItem.cancel(self.row.id, self.cancelItemObj).then(function (res) {
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.cancelSuccess"),
            type: "success",
          });
          self.loadData();
        } else {
          self.sysError();
        }
        self.cancelItemDoing = false;
        self.cancelItemDF = false;
      });
    },
    // 入库
    inItem(row) {
      const self = this;
      self.row = row;
      self.actionName = "inItem";
      self.inObj.remark = null;
      self.inDoing = false;
      self.inDF = true;
    },
    inAll(row) {
      const self = this;
      self.row = row;
      self.actionName = "inAll";
      self.inObj.remark = null;
      self.inDoing = false;
      self.inDF = true;
    },
    inSubmit() {
      const self = this;
      if (self.actionName == "inItem") {
        self.rentItemInFun();
      }
      if (self.actionName == "inAll") {
        self.rentInFun();
      }
    },
    rentItemInFun() {
      const self = this;
      self.inDoing = true;
      RentItem.in(self.row.id, self.inObj).then(function (res) {
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.stockInSuccess"),
            type: "success",
          });
          self.loadData();
        } else {
          self.sysError();
        }
        self.inDoing = false;
        self.inDF = false;
      });
    },
    rentInFun() {
      const self = this;
      self.inDoing = true;
      Rent.finish(self.row.id, self.inObj).then(function (res) {
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.stockInSuccess"),
            type: "success",
          });
          self.loadData();
        } else {
          self.sysError();
        }
        self.inDoing = false;
        self.inDF = false;
      });
    },
    // 出库
    outItem(row) {
      const self = this;
      self.row = row;
      self.actionName = "outItem";
      self.outObj.remark = null;
      self.outDoing = false;
      self.outDF = true;
    },
    outAll(row) {
      const self = this;
      self.row = row;
      self.actionName = "outAll";
      self.outObj.remark = null;
      self.outDoing = false;
      self.outDF = true;
    },
    outSubmit() {
      const self = this;
      if (self.actionName == "outItem") {
        self.rentItemOutFun();
      }
      if (self.actionName == "outAll") {
        self.rentOutFun();
      }
    },
    rentItemOutFun() {
      const self = this;
      self.outDoing = true;
      RentItem.out(self.row.id, self.outObj).then(function (res) {
        if (res.status === 200) {
          self.sysResult(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
        self.outDoing = false;
        self.outDF = false;
      });
    },
    rentOutFun() {
      const self = this;
      self.outDoing = true;
      Rent.out(self.row.id, self.outObj).then(function (res) {
        if (res.status === 200) {
          self.sysResults(res.data, self.$t("prompt.stockOutSuccess"));
          self.loadData();
        } else {
          self.sysError();
        }
        self.outDoing = false;
        self.outDF = false;
      });
    },

    //
    canStockInAll(row) {
      if (row.items.length != 0) {
        const workflowStates = row.items.map((i) => i.workflowState);
        const workflowStatesSet = [...new Set(workflowStates)];
        if (workflowStatesSet.length == 1 && workflowStatesSet[0] == "outed") {
          return true;
        }
      }
      return false;
    },
    canStockOutAllClean(row) {
      if (row.items.length != 0) {
        const workflowStates = row.items.map((i) => i.workflowState);
        const workflowStatesSet = [...new Set(workflowStates)];
        if (workflowStatesSet.length == 1 && workflowStatesSet[0] == "newly") {
          return true;
        }
      }
      return false;
    },
    canStockOutAllRent(row) {
      let itemStates = [];
      let orderStates = [];
      if (row.items.length != 0) {
        for (const item of row.items) {
          itemStates.push(item.workflowState);
          if (!["newly", "outed", "finished"].includes(item.workflowState)) {
            return false;
          }
        }
      }
      if (row.orders.length != 0) {
        for (const order of row.orders) {
          orderStates.push(order.workflowState);
          if (
            !["prepared", "qualitied", "finished"].includes(order.workflowState)
          ) {
            return false;
          }
        }
      }
      let itemStatesSet = [...new Set(itemStates)];
      let orderStatesSet = [...new Set(orderStates)];
      if (
        itemStatesSet.length == 1 &&
        ["outed", "finished"].includes(itemStatesSet[0]) &&
        orderStatesSet.length == 0
      ) {
        return false;
      }
      if (
        itemStatesSet.length == 0 &&
        orderStatesSet.length == 1 &&
        itemStatesSet[0] == "finished"
      ) {
        return false;
      }
      if (
        itemStatesSet.length == 1 &&
        ["outed", "finished"].includes(itemStatesSet[0]) &&
        orderStatesSet.length == 1 &&
        itemStatesSet[0] == "finished"
      ) {
        return false;
      }
      return true;
    },

    // 删除子订单
    deleteItem(row) {
      const self = this;
      if (["available", "custom"].includes(row.type)) {
        Order.delete(row.id).then(function (res) {
          if (res.status === 200) {
            self.$message({
              message: self.$t("prompt.delSuccess"),
              type: "success",
            });
            self.loadData();
          } else {
            self.sysError();
          }
        });
      } else {
        RentItem.delete(row.id).then(function (res) {
          if (res.status === 200) {
            self.$message({
              message: self.$t("prompt.delSuccess"),
              type: "success",
            });
            self.loadData();
          } else {
            self.sysError();
          }
        });
      }
    },
    // 编辑
    editItem(row, rowIndex) {
      this.row = row;
      this.currentRowItemId = row.id;
      this.currentRowIdx = rowIndex;
      this.editRentItemDF = true;
    },
    itemUpdated(rentItem) {
      for (const key in this.records[this.currentRowIdx]) {
        this.records[this.currentRowIdx][key] = rentItem[key];
      }
      this.editRentItemDF = false;
    },
    showItem(row) {
      this.row = row;
      this.currentRowItemId = row.id;
      this.showItemDF = true;
    },

    //=========================================================
    // 打印客户清单
    printSlip(row, cmd) {
      const self = this;
      self.printRent = row;
      self.printRent.items = self.printRent.items.filter(
        (i) => i.workflowState != "canceled"
      )
      // .concat(self.printRent.orders.filter(
      //   (i) => i.workflowState != "canceled"
      // ));
      self.$nextTick(function () {
        if (
          [
            "printRentSlipDiv",
            "printRentSlipDiv2",
            "printRentSlipDiv3",
          ].includes(cmd)
        ) {
          self.print(cmd);
        } else {
          self.customPrintSubmit(cmd);
        }
      });
    },
    print(type) {
      const newWindow = window.open("_blank");
      const codestr = document.getElementById(type).innerHTML;
      newWindow.document.write(codestr);
      newWindow.document.close();
    },
    customPrintSubmit(cmd) {
      const self = this;
      self.customPrintContent = cmd.content;
      let currencySymbol = "¥";
      self.customPrintContent = self.customPrintContent.replace(
        /\${单号}/g,
        self.printRent.serialNo
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${合计}/g,
        currencySymbol + self.printRent.amount
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${收款额}/g,
        currencySymbol + self.printRent.payAmount
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${欠款额}/g,
        currencySymbol + self.printRent.diffAmount
      );
      let paymentsStrArr = self.printRent.payments.map(
        (p) =>
          p.payMethodName +
          "(" +
          currencySymbol +
          p.total +
          " " +
          p.paidAt +
          ")"
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${收款方式}/g,
        paymentsStrArr.join(";")
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${所属}/g,
        self.printRent.ownerName
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${店铺}/g,
        self.printRent.shopName
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${创建日期}/g,
        self.printRent.createAt
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${备注}/g,
        self.printRent.remark != null
          ? self.printRent.remark.replace(
              /<p>/g,
              "<p style='margin: 0; padding: 0'>"
            )
          : ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${客户姓名}/g,
        self.printRent.customerName
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${客户婚期}/g,
        self.printRent.customerWeddingDay
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${客户电话}/g,
        self.printRent.customerPhone
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${客户省份}/g,
        self.printRent.customerProvince || ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${客户城市}/g,
        self.printRent.customerCity || ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${客户地址}/g,
        self.printRent.customerAddress || ""
      );
      if (
        self.customPrintContent.indexOf("订单项") != -1 &&
        self.printRent.items &&
        self.printRent.items.length != 0
      ) {
        let rentItemContentTitle =
          "<table style='width: 100%; border-collapse:collapse; border: 1px solid black; text-align: left; margin-bottom: 20px; padding: 5px'>" +
          "<tr style='border: 1px solid black;  text-align: center'>" +
          "<td style='border: 1px solid black; width: 60px;'>序号</td>";
        if (self.customPrintContent.indexOf("图片") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;width: 80px'>图片</td>";
        }
        if (self.customPrintContent.indexOf("项号") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;min-width: 100px'>项号</td>";
        }
        if (self.customPrintContent.indexOf("货号") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;min-width: 80px'>货号</td>";
        }
        if (self.customPrintContent.indexOf("分类") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;min-width: 80px'>分类</td>";
        }
        if (self.customPrintContent.indexOf("品牌") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;min-width: 80px'>品牌</td>";
        }
        if (self.customPrintContent.indexOf("颜色") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;min-width: 60px'>颜色</td>";
        }
        if (self.customPrintContent.indexOf("规格") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;min-width: 60px'>规格</td>";
        }
        if (self.customPrintContent.indexOf("单价") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;min-width: 60px'>单价</td>";
        }
        if (self.customPrintContent.indexOf("数量") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;min-width: 60px'>数量</td>";
        }
        if (self.customPrintContent.indexOf("使用日期") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;min-width: 80px'>使用日期</td>";
        }
        if (self.customPrintContent.indexOf("单项创建日期") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;min-width: 80px'>单项创建日期</td>";
        }
        if (self.customPrintContent.indexOf("单项备注") != -1) {
          rentItemContentTitle =
            rentItemContentTitle +
            "<td style='border: 1px solid black;min-width: 120px'>单项备注</td>";
        }
        rentItemContentTitle = rentItemContentTitle + "</tr>";

        let rentItemContents = "";
        let i = 1;
        for (const item of self.printRent.items) {
          let rentItemContentTr =
            "<tr style='border: 1px solid black;  text-align: center'>" +
            "<td style='border: 1px solid black;'>" +
            i +
            "</td>";
          i++;
          if (self.customPrintContent.indexOf("图片") != -1) {
            if (item.images != null) {
              rentItemContentTr =
                rentItemContentTr +
                "<td style='border: 1px solid black;'>" +
                "<image src='" +
                item.images.split(";")[0] +
                "' style='width: 80px'/>" +
                "</td>";
            } else {
              rentItemContentTr =
                rentItemContentTr +
                "<td style='border: 1px solid black;'>" +
                " " +
                "</td>";
            }
          }
          if (self.customPrintContent.indexOf("项号") != -1) {
            rentItemContentTr =
              rentItemContentTr +
              "<td style='border: 1px solid black;'>" +
              item.serialNo +
              "</td>";
          }
          if (self.customPrintContent.indexOf("货号") != -1) {
            rentItemContentTr =
              rentItemContentTr +
              "<td style='border: 1px solid black;'>" +
              item.productNo +
              "</td>";
          }
          if (self.customPrintContent.indexOf("分类") != -1) {
            rentItemContentTr =
              rentItemContentTr +
              "<td style='border: 1px solid black;'>" +
              (item.categoryName || "") +
              "</td>";
          }
          if (self.customPrintContent.indexOf("品牌") != -1) {
            rentItemContentTr =
              rentItemContentTr +
              "<td style='border: 1px solid black;'>" +
              (item.brand || "") +
              "</td>";
          }
          if (self.customPrintContent.indexOf("颜色") != -1) {
            rentItemContentTr =
              rentItemContentTr +
              "<td style='border: 1px solid black;'>" +
              (item.color || "") +
              "</td>";
          }
          if (self.customPrintContent.indexOf("规格") != -1) {
            rentItemContentTr =
              rentItemContentTr +
              "<td style='border: 1px solid black;'>" +
              (item.size || "") +
              "</td>";
          }
          if (self.customPrintContent.indexOf("单价") != -1) {
            rentItemContentTr =
              rentItemContentTr +
              "<td style='border: 1px solid black;'>" +
              currencySymbol +
              item.price +
              "</td>";
          }
          if (self.customPrintContent.indexOf("数量") != -1) {
            rentItemContentTr =
              rentItemContentTr +
              "<td style='border: 1px solid black;'>" +
              item.quantity +
              (item.unit || "") +
              "</td>";
          }
          if (self.customPrintContent.indexOf("使用日期") != -1) {
            rentItemContentTr =
              rentItemContentTr +
              "<td style='border: 1px solid black;'>" +
              item.startAt +
              "<div>" +
              item.endAt +
              "</div>";
            ("</td>");
          }
          if (self.customPrintContent.indexOf("单项创建日期") != -1) {
            rentItemContentTr =
              rentItemContentTr +
              "<td style='border: 1px solid black;'>" +
              item.createAt +
              "</td>";
          }
          if (self.customPrintContent.indexOf("单项备注") != -1) {
            let itemRemark = "";
            if (item.remark != null) {
              itemRemark = item.remark.replace(
                /<p>/g,
                "<p style='margin: 0; padding: 0'>"
              );
            }
            rentItemContentTr =
              rentItemContentTr +
              "<td style='border: 1px solid black;'>" +
              itemRemark +
              "</td>";
          }
          rentItemContents = rentItemContents + rentItemContentTr + "</tr>";
        }
        self.customPrintContent = self.customPrintContent.replace(
          /\${订单项}/g,
          rentItemContentTitle + rentItemContents + "</table>"
        );
      } else {
        self.customPrintContent = self.customPrintContent.replace(
          /\${订单项}/g,
          ""
        );
      }
      self.customPrintContent = self.customPrintContent.replace(
        /\${图片}/g,
        ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${项号}/g,
        ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${货号}/g,
        ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${分类}/g,
        ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${品牌}/g,
        ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${颜色}/g,
        ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${规格}/g,
        ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${单价}/g,
        ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${数量}/g,
        ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${使用日期}/g,
        ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${单项创建日期}/g,
        ""
      );
      self.customPrintContent = self.customPrintContent.replace(
        /\${单项备注}/g,
        ""
      );
      window.document.getElementById("customPrintContentRentSlip").innerHTML =
        self.customPrintContent;
      self.print("printRentSlipCustomDiv");
    },
    // 添加收款
    addPayment(row, index) {
      const self = this;
      self.currentRowIdx = index;
      self.addPaymentObj.rentId = row.id;
      self.addPaymentObj.paidAt = Util.formatDate(new Date());
      self.addPaymentObj.total = null;
      self.addPaymentObj.explain = null;
      self.addPaymentObj.voucher = null;
      self.addPaymentObj.type = "receivables";
      self.addPaymentObj.customerId = row.customerId;
      self.getPayMethods();
      self.addPaymentDoing = false;
      self.addPaymentDF = true;
    },
    addPaymentSubmit() {
      const self = this;
      self.$refs.addPaymentObjForm.validate((valid) => {
        if (valid) {
          if (!Util.regexQuantity(self.addPaymentObj.total)) {
            self.$message({
              message: self.$t("rules.regex", [self.$t("payment.total")]),
              type: "warning",
            });
            return;
          }
          if (self.addPaymentObj.type === "return") {
            self.addPaymentObj.total = -self.addPaymentObj.total;
          }
          self.addPaymentObj.payMethodName = self.payMethods.find(
            (p) => p.id === self.addPaymentObj.payMethodId
          ).name;
          self.addPaymentObj.paidAt = Util.formatDate(
            self.addPaymentObj.paidAt
          );
          self.addPaymentDoing = true;
          Payment.create(self.addPaymentObj).then(function (res) {
            if (res.status === 200) {
              self.$message({
                message: self.$t("prompt.addSuccess"),
                type: "success",
              });
              self.loadData();
            } else {
              self.sysError();
            }
            self.addPaymentDoing = false;
            self.addPaymentDF = false;
          });
        } else {
          return false;
        }
      });
    },
    // 添加押金
    addDeposit(row, index) {
      const self = this;
      self.currentRowIdx = index;
      self.row = row;
      self.addDepositObj.total = null;
      self.addDepositObj.payMethodId = null;
      self.addDepositObj.paidAt = Util.formatDate(new Date());
      self.addDepositObj.remark = null;
      self.addDepositObj.voucher = null;
      self.getPayMethods();
      self.addDepositDoing = false;
      self.addDepositDF = true;
    },
    addDepositSubmit() {
      const self = this;
      self.$refs.addDepositObjForm.validate((valid) => {
        if (valid) {
          if (!Util.regexQuantity(self.addDepositObj.total)) {
            self.$message({
              message: self.$t("rules.regex", [self.$t("payment.total")]),
              type: "warning",
            });
            return;
          }

          self.addPaymentObj.addDepositName = self.payMethods.find(
            (p) => p.id === self.addDepositObj.payMethodId
          ).name;
          self.addDepositObj.paidAt = Util.formatDate(
            self.addDepositObj.paidAt
          );
          self.addDepositDoing = true;
          Rent.addDepoist(self.row.id, self.addDepositObj).then(function (res) {
            if (res.status === 200) {
              self.$message({
                message: self.$t("prompt.addSuccess"),
                type: "success",
              });
              self.loadData();
            } else {
              self.sysError();
            }
            self.addDepositDoing = false;
            self.addDepositDF = false;
          });
        } else {
          return false;
        }
      });
    },
    // 分配拥有人
    distributiveOwner(row, index) {
      const self = this;
      self.row = row;
      self.currentRowIdx = index;
      self.usersFilterValid = self.users.filter((u) => u.voidAt == null);
      if (self.users.find((u) => u.id === row.ownerId) != null) {
        self.distributiveOwnerObj.ownerId = row.ownerId;
      }
      self.distributiveOwnerDoing = false;
      self.distributiveOwnerDF = true;
    },
    distributiveOwnerSubmit() {
      const self = this;
      self.$refs.distributiveOwnerObjForm.validate((valid) => {
        if (valid) {
          self.distributiveOwnerObj.ownerName = self.users.find(
            (u) => u.id === self.distributiveOwnerObj.ownerId
          ).name;
          self.distributiveOwnerDoing = true;
          Rent.updataOwner(self.row.id, self.distributiveOwnerObj).then(
            function (res) {
              self.distributiveOwnerDoing = false;
              self.distributiveOwnerDF = false;
              if (res.status === 200) {
                self.$message({
                  message: self.$t("prompt.distributiveSuccess"),
                  type: "success",
                });
                self.records[self.currentRowIdx].ownerName =
                  self.distributiveOwnerObj.ownerName;
              } else {
                self.sysError();
              }
            }
          );
        } else {
          return false;
        }
      });
    },
    //删除
    deleteRent(row) {
      const self = this;
      Rent.delete(row.id).then(function (res) {
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.delSuccess"),
            type: "success",
          });
          self.loadData();
        } else {
          self.sysError();
        }
      });
    },
    //编辑
    canEditRent(row) {
      if (row.items && row.items.length !== 0) {
        const canEditRentItems = row.items.filter(
          (item) => item.workflowState === "newly"
        );
        if (canEditRentItems.length === row.items.length) {
          return true;
        }
        return false;
      } else {
        return true;
      }
    },
    editRent(row, rowIndex) {
      this.row = row;
      this.currentRowRentId = row.id;
      this.currentRowIdx = rowIndex;
      this.editRentDF = true;
    },
    rentUpdated(rent) {
      this.records[this.currentRowIdx].serialNo = rent.serialNo;
      this.records[this.currentRowIdx].remark = rent.remark;
      this.records[this.currentRowIdx].startAt = rent.startAt;
      this.records[this.currentRowIdx].endAt = rent.endAt;
      this.records[this.currentRowIdx].amount = rent.amount;
      this.records[this.currentRowIdx].customerName = rent.customerName;
      this.editRentDF = false;
    },

    //========================
    showRent(row) {
      this.row = row;
      this.currentRowRentId = row.id;
      this.showRentDF = true;
    },
    showRentUpdated() {
      this.loadData();
    },
    //
    dataProvider(params) {
      return Rent.search(params);
    },
    recordsDecorate(records) {
      this.records = this.buildTableData(records);
      this.mergeCells = this.buildMergeCells(records);
    },
    buildTableData(records) {
      let rs = [];
      records.forEach((record) => {
        let bRent = {
          isMerge: true,
          id: record.id,
          serialNo: record.serialNo,
          customerId: record.customerId,
          customerName: record.customerName,
          customerCountry: record.customerCountry,
          customerProvince: record.customerProvince,
          customerCity: record.customerCity,
          customerAddress: record.customerAddress,
          customerPhone: record.customerPhone,
          customerZipcode: record.customerZipcode,
          customerWeddingDay: record.customerWeddingDay,
          remark: record.remark,
          cancelAt: record.cancelAt,
          cancelReason: record.cancelReason,
          ownerId: record.ownerId,
          ownerName: record.ownerName,
          shopId: record.shopId,
          shopName: record.shopName,
          createAt: record.createAt,
          tenantId: record.tenantId,
          startAt: record.startAt,
          endAt: record.endAt,
          customerSource: record.customerSource,
          amount: record.amount,
          payAmount: record.payAmount,
          diffAmount: record.diffAmount,
          items: record.items,
          payments: record.payments,
          type: record.type,
          packageId: record.packageId,
          packageName: record.packageName,
          packageAmount: record.packageAmount,
          scheduleId: record.scheduleId,
          deposit: record.deposit,
          depositAmount: record.depositAmount,
          depositReturnAmount: record.depositReturnAmount,
          serviceName: record.serviceName,
          serviceAmount: record.serviceAmount,
          items: record.items,
          orders: record.orders,
        };
        rs.push(bRent); //将bargain的数据转换到格式，放到数据列表

        record.items.concat(record.orders).forEach((item) => {
          Vue.set(item, "_rent", record);
          rs.push(item);
        });
      });
      return rs;
    },
    buildMergeCells(rents) {
      let mcs = [];
      for (let i = 0, rowIdx = 0; i < rents.length; i++) {
        mcs.push({ row: rowIdx, col: 1, rowspan: 1, colspan: 11 });
        rowIdx = rowIdx + rents[i].items.length + rents[i].orders.length + 1;
      }
      return mcs;
    },
    calculateBgColor({ row, rowIndex }) {
      return row.isMerge ? "light-blue-bg" : "";
    },

    selectedBargains() {
      return this.$refs.recordsTable
        .getCheckboxRecords()
        .filter((r) => r.isMerge);
    },
    selectedOrders() {
      return this.$refs.recordsTable
        .getCheckboxRecords()
        .filter((r) => !r.isMerge);
    },
    selectRecord({ rowIndex, selection, checked, row, data }) {
      if (row.isMerge) {
        let items = data.filter((r) => r.rentId == row.id);
        this.$refs.recordsTable.setCheckboxRow(items, checked);
      }
      this.setSelectedQty();
    },
    selectAllRecords() {
      this.setSelectedQty();
    },
  },
};
</script>
<style scoped>
</style>